import React, { useState, useEffect } from 'react';
import {
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonButton,
  IonIcon,
  IonText,
  IonSpinner,
  IonAlert,
  IonToast,
  IonChip,
  IonLabel,
  IonItem,
  IonList,
  IonNote,
} from '@ionic/react';
import { 
  logoWhatsapp, 
  linkOutline, 
  unlinkOutline, 
  copyOutline, 
  checkmarkCircle,
  alertCircle,
  timeOutline,
  chatbubbleOutline,
} from 'ionicons/icons';
import { whatsappService } from '../services/api';

interface WhatsAppStatus {
  success: boolean;
  isLinked: boolean;
  phoneNumber?: string;
  linkedAt?: string;
  hasActiveToken?: boolean;
  tokenExpiresAt?: string;
}

const WhatsAppIntegration: React.FC = () => {
  const [status, setStatus] = useState<WhatsAppStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const [linkingToken, setLinkingToken] = useState<string | null>(null);
  const [tokenExpires, setTokenExpires] = useState<Date | null>(null);
  const [showUnlinkAlert, setShowUnlinkAlert] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  useEffect(() => {
    loadWhatsAppStatus();
  }, []);

  const openWhatsAppChat = () => {
    const phoneNumber = '15556382524'; // +1 (555) 638-2524 formatted for WhatsApp
    const message = 'Hi! I want to link my ManageTix account to WhatsApp.';
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const loadWhatsAppStatus = async () => {
    try {
      setLoading(true);
      const result = await whatsappService.getWhatsAppStatus();
      setStatus(result);
    } catch (error) {
      console.error('Error loading WhatsApp status:', error);
      setToastMessage('Failed to load WhatsApp status');
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  const generateToken = async () => {
    try {
      setGenerating(true);
      const result = await whatsappService.generateLinkingToken();
      
      if (result.success) {
        setLinkingToken(result.token);
        setTokenExpires(new Date(result.expiresAt));
        setToastMessage('Linking token generated successfully!');
        setShowToast(true);
        await loadWhatsAppStatus(); // Refresh status
      } else {
        setToastMessage('Failed to generate linking token');
        setShowToast(true);
      }
    } catch (error) {
      console.error('Error generating token:', error);
      setToastMessage('Failed to generate linking token');
      setShowToast(true);
    } finally {
      setGenerating(false);
    }
  };

  const copyToken = async () => {
    if (linkingToken) {
      try {
        await navigator.clipboard.writeText(linkingToken);
        setToastMessage('Token copied to clipboard!');
        setShowToast(true);
      } catch (error) {
        console.error('Failed to copy token:', error);
        setToastMessage('Failed to copy token');
        setShowToast(true);
      }
    }
  };

  const unlinkWhatsApp = async () => {
    try {
      setUnlinking(true);
      const result = await whatsappService.unlinkWhatsApp();
      
      if (result.success) {
        setToastMessage('WhatsApp unlinked successfully');
        setShowToast(true);
        setLinkingToken(null);
        setTokenExpires(null);
        await loadWhatsAppStatus(); // Refresh status
      } else {
        setToastMessage('Failed to unlink WhatsApp');
        setShowToast(true);
      }
    } catch (error) {
      console.error('Error unlinking WhatsApp:', error);
      setToastMessage('Failed to unlink WhatsApp');
      setShowToast(true);
    } finally {
      setUnlinking(false);
      setShowUnlinkAlert(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const isTokenExpired = (expireDate: Date) => {
    return new Date() > expireDate;
  };

  if (loading) {
    return (
      <IonCard>
        <IonCardContent className="text-center">
          <IonSpinner />
          <IonText>
            <p>Loading WhatsApp integration status...</p>
          </IonText>
        </IonCardContent>
      </IonCard>
    );
  }

  return (
    <>
      <IonCard>
        <IonCardHeader>
          <IonCardTitle className="flex items-center">
            <IonIcon icon={logoWhatsapp} className="mr-2 text-green-500" size="large" />
            WhatsApp Integration
          </IonCardTitle>
        </IonCardHeader>
        <IonCardContent>
          {/* Status Section */}
          <div className="mb-4">
            <IonList>
              <IonItem>
                <IonIcon 
                  icon={status?.isLinked ? checkmarkCircle : alertCircle} 
                  slot="start" 
                  color={status?.isLinked ? 'success' : 'warning'} 
                />
                <IonLabel>
                  <h3>Status</h3>
                  <p>{status?.isLinked ? 'Connected' : 'Not Connected'}</p>
                </IonLabel>
                <IonChip 
                  slot="end" 
                  color={status?.isLinked ? 'success' : 'medium'}
                >
                  {status?.isLinked ? 'Linked' : 'Unlinked'}
                </IonChip>
              </IonItem>

              {status?.isLinked && status.phoneNumber && (
                <IonItem>
                  <IonIcon icon={logoWhatsapp} slot="start" color="success" />
                  <IonLabel>
                    <h3>Phone Number</h3>
                    <p>{status.phoneNumber}</p>
                  </IonLabel>
                </IonItem>
              )}

              {status?.isLinked && status.linkedAt && (
                <IonItem>
                  <IonIcon icon={timeOutline} slot="start" />
                  <IonLabel>
                    <h3>Linked At</h3>
                    <p>{formatDate(status.linkedAt)}</p>
                  </IonLabel>
                </IonItem>
              )}
            </IonList>
          </div>

          {/* Current Token Section */}
          {status?.hasActiveToken && status.tokenExpiresAt && (
            <div className="mb-4">
              <IonText color="warning">
                <h4>Active Linking Token</h4>
                <p>You have an active token that expires at: {formatDate(status.tokenExpiresAt)}</p>
              </IonText>
            </div>
          )}

          {/* Generated Token Display */}
          {linkingToken && tokenExpires && (
            <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <IonText color="primary">
                <h4>Your Linking Token</h4>
              </IonText>
              <div className="flex items-center justify-between mt-2 p-3 bg-white dark:bg-gray-800 rounded border">
                <code className="text-sm font-mono break-all">{linkingToken}</code>
                <IonButton 
                  fill="clear" 
                  size="small" 
                  onClick={copyToken}
                >
                  <IonIcon icon={copyOutline} />
                </IonButton>
              </div>
              <IonNote color={isTokenExpired(tokenExpires) ? 'danger' : 'medium'}>
                {isTokenExpired(tokenExpires) 
                  ? 'Token expired' 
                  : `Expires: ${tokenExpires.toLocaleString()}`
                }
              </IonNote>
              
              <div className="mt-3">
                <IonText>
                  <h5>Instructions:</h5>
                  <ol className="list-decimal list-inside space-y-1 text-sm">
                    <li>Copy the token above</li>
                    <li>Click the button below to open WhatsApp chat</li>
                    <li>Send the token as a message</li>
                    <li>Your account will be automatically linked!</li>
                  </ol>
                </IonText>
                
                <div className="mt-3">
                  <IonButton 
                    expand="block" 
                    fill="outline" 
                    color="success"
                    onClick={openWhatsAppChat}
                  >
                    <IonIcon icon={chatbubbleOutline} slot="start" />
                    Open WhatsApp Chat
                  </IonButton>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            {!status?.isLinked ? (
              <IonButton 
                expand="block" 
                onClick={generateToken}
                disabled={generating}
              >
                {generating ? (
                  <>
                    <IonSpinner slot="start" />
                    Generating...
                  </>
                ) : (
                  <>
                    <IonIcon icon={linkOutline} slot="start" />
                    Generate Linking Token
                  </>
                )}
              </IonButton>
            ) : (
              <IonButton 
                expand="block" 
                fill="outline" 
                color="danger"
                onClick={() => setShowUnlinkAlert(true)}
                disabled={unlinking}
              >
                <IonIcon icon={unlinkOutline} slot="start" />
                Unlink WhatsApp
              </IonButton>
            )}

            {status?.isLinked && (
              <IonButton 
                expand="block" 
                fill="outline"
                onClick={generateToken}
                disabled={generating}
              >
                {generating ? (
                  <>
                    <IonSpinner slot="start" />
                    Generating...
                  </>
                ) : (
                  <>
                    <IonIcon icon={linkOutline} slot="start" />
                    Generate New Token
                  </>
                )}
              </IonButton>
            )}
          </div>

          {/* Information Section */}
          <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <IonText>
              <h4>About WhatsApp Integration</h4>
              <p className="text-sm mt-2">
                Link your WhatsApp to interact with ManageTix AI assistant. Once connected, you can:
              </p>
              <ul className="list-disc list-inside text-sm mt-2 space-y-1">
                <li>Add tickets by sending ticket details or photos</li>
                <li>Create sales through chat</li>
                <li>Check inventory and event information</li>
                <li>Get real-time assistance with your ticket business</li>
              </ul>
            </IonText>
          </div>
        </IonCardContent>
      </IonCard>

      {/* Unlink Confirmation Alert */}
      <IonAlert
        isOpen={showUnlinkAlert}
        onDidDismiss={() => setShowUnlinkAlert(false)}
        header="Unlink WhatsApp"
        message="Are you sure you want to unlink your WhatsApp? You'll need to generate a new token to reconnect."
        buttons={[
          {
            text: 'Cancel',
            role: 'cancel',
          },
          {
            text: 'Unlink',
            role: 'destructive',
            handler: unlinkWhatsApp,
          },
        ]}
      />

      {/* Toast for notifications */}
      <IonToast
        isOpen={showToast}
        onDidDismiss={() => setShowToast(false)}
        message={toastMessage}
        duration={3000}
        position="bottom"
      />
    </>
  );
};

export default WhatsAppIntegration;